/**
 * SaveToLibraryDialog — publish a completed Chart Visualization plan to
 * the executive prompt library.
 *
 * Launched from the "Save to prompt library" action on a CV result card
 * (result-card placement is deliberate: the admin verifies the rendered
 * answer before anything becomes executive-visible; the backend enforces
 * the same rule by only accepting completed CV plans). The saved item
 * stores the plan's frozen Params; scenarios stay late-bound to the
 * executive's own selection at execution time.
 */
import React, { useState } from 'react';
import { Autocomplete, Stack, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';

import PopupDialog from 'src/components/shared/PopupDialog';

import { saveToLibrary } from './api';

// Seed suggestions only — Category is free text; the library groups by
// whatever categories Trinity authors settle on.
const CATEGORY_SUGGESTIONS = [
  'Revenue & Growth',
  'Patient Flow',
  'Market Share',
  'Scenarios & Risk',
];

interface SaveToLibraryDialogProps {
  planId: string | null;
  onClose: () => void;
}

const SaveToLibraryDialog: React.FC<SaveToLibraryDialogProps> = ({
  planId,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTitle('');
    setSubtitle('');
    setCategory('');
    setSaving(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = async () => {
    if (!planId) return;
    setSaving(true);
    try {
      await saveToLibrary(
        planId,
        title.trim(),
        subtitle.trim() || null,
        category.trim()
      );
      enqueueSnackbar('Saved to the executive prompt library.', {
        variant: 'success',
      });
      handleClose();
    } catch (e) {
      setSaving(false);
      enqueueSnackbar(
        e instanceof Error ? e.message : 'Failed to save to the library.',
        { variant: 'error' }
      );
    }
  };

  return (
    <PopupDialog
      open={planId !== null}
      close={handleClose}
      title="Save to Prompt Library"
      maxWidth="sm"
      primaryButtons={[
        {
          id: 'save',
          label: 'Save',
          onClick: handleSave,
          disabled: !title.trim() || !category.trim() || saving,
          loading: saving,
          variant: 'contained',
        },
      ]}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          label="Executive-facing question"
          placeholder="e.g. What is the latest revenue forecast through 2030?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          inputProps={{ maxLength: 255 }}
        />
        <TextField
          label="Subtitle (optional)"
          placeholder="Short supporting description"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          fullWidth
          inputProps={{ maxLength: 255 }}
        />
        <Autocomplete
          freeSolo
          options={CATEGORY_SUGGESTIONS}
          inputValue={category}
          onInputChange={(_, value) => setCategory(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Category"
              required
              inputProps={{ ...params.inputProps, maxLength: 100 }}
            />
          )}
        />
      </Stack>
    </PopupDialog>
  );
};

export default SaveToLibraryDialog;
